import React from 'react';
import { Box, Typography, Chip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import SpeedIcon from '@material-ui/icons/Speed';
import SectionTitle from '../common/SectionTitle';
import GlassCard from '../common/GlassCard';
import { t } from '../../i18n';
import { getDrivingSummary } from '../../utils/tachograph';

const STOP_CONFIG = {
  break_45:           { color: '#f59e0b', bg: 'rgba(245,158,11,0.08)', border: 'rgba(245,158,11,0.25)', labelPt: 'Pausa', labelEn: 'Break' },
  daily_rest:         { color: '#8b5cf6', bg: 'rgba(139,92,246,0.08)', border: 'rgba(139,92,246,0.25)', labelPt: 'Descanso diário', labelEn: 'Daily rest' },
  daily_rest_reduced: { color: '#a855f7', bg: 'rgba(168,85,247,0.08)', border: 'rgba(168,85,247,0.25)', labelPt: 'Descanso reduzido', labelEn: 'Reduced rest' },
  weekly_rest:        { color: '#ef4444', bg: 'rgba(239,68,68,0.08)',  border: 'rgba(239,68,68,0.25)',  labelPt: 'Descanso semanal', labelEn: 'Weekly rest' },
};

const useStyles = makeStyles(() => ({
  timeline: {
    position: 'relative',
    paddingLeft: 20,
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 7,
      top: 8,
      bottom: 8,
      width: 1,
      background: 'rgba(0,0,0,0.08)',
    },
  },
  timelineItem: {
    position: 'relative',
    marginBottom: 10,
    '&::before': {
      content: '""',
      position: 'absolute',
      left: -16,
      top: 4,
      width: 7,
      height: 7,
      borderRadius: '50%',
      border: '1.5px solid',
    },
  },
  driveItem: {
    '&::before': { borderColor: '#3b82f6', background: 'rgba(59,130,246,0.15)' },
  },
  stopItem: {
    '&::before': { borderColor: '#f59e0b', background: 'rgba(245,158,11,0.15)' },
  },
  restItem: {
    '&::before': { borderColor: '#8b5cf6', background: 'rgba(139,92,246,0.15)' },
  },
  weeklyRestItem: {
    '&::before': { borderColor: '#ef4444', background: 'rgba(239,68,68,0.15)' },
  },
  endItem: {
    '&::before': { borderColor: '#22c55e', background: 'rgba(34,197,94,0.15)' },
  },
  label: { fontSize: '0.72rem', color: '#0f172a', fontWeight: 500 },
  sublabel: { fontSize: '0.62rem', color: '#64748b' },
}));

function formatMin(min) {
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h === 0) return `${m}min`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}min`;
}

function TachographPanel({ route, lang }) {
  const classes = useStyles();
  const cr = route.computedRoute;
  if (!cr) return null;

  const totalDriveMin = Math.round(cr.duration / 60);
  const stops = route.stops || [];
  const isPt = lang === 'pt';

  // Summary
  const summary = getDrivingSummary(stops, totalDriveMin);

  // Build timeline segments
  const segments = [];
  let driven = 0;
  let driveCount = 0;

  stops.forEach((stop, idx) => {
    // Drive segment before this stop
    const segDrive = (stop.drivingMinBefore || 0) - driven;
    if (segDrive > 0) {
      driveCount++;
      segments.push({
        type: 'drive',
        duration: segDrive,
        label: `${t(lang, 'driving')} ${driveCount}`,
        sublabel: formatMin(segDrive),
      });
      driven += segDrive;
    }

    // Stop/rest segment
    const cfg = STOP_CONFIG[stop.type] || STOP_CONFIG.break_45;
    const locationStr = stop.suggestedRestArea?.name
      || stop.location
      || `Km ${stop.km}`;
    const timeStr = stop.arrivalTime
      ? (stop.arrivalDate ? `${stop.arrivalDate} ${stop.arrivalTime}` : stop.arrivalTime)
      : '';

    segments.push({
      type: stop.type.includes('daily_rest') ? 'rest' : stop.type === 'weekly_rest' ? 'weekly_rest' : 'stop',
      stopType: stop.type,
      duration: stop.duration,
      label: stop.label || (isPt ? cfg.labelPt : cfg.labelEn),
      sublabel: `${formatMin(stop.duration)} · ${locationStr}${timeStr ? ` · ${timeStr}` : ''}`,
      preferred: stop.suggestedRestArea?.preferred,
      tachoAdjusted: stop.tachoAdjusted,
    });
  });

  // Final drive segment
  const lastDrive = totalDriveMin - driven;
  if (lastDrive > 0) {
    driveCount++;
    segments.push({
      type: 'drive',
      label: stops.length > 0 ? t(lang, 'finalDriving') : t(lang, 'driving'),
      sublabel: formatMin(lastDrive),
      duration: lastDrive,
    });
  }

  segments.push({ type: 'end', label: t(lang, 'arrival'), sublabel: '' });

  return (
    <Box>
      <SectionTitle icon={SpeedIcon}>{t(lang, 'tachographTitle')}</SectionTitle>
      <GlassCard flat padding="sm">
        {/* Summary badges */}
        {stops.length > 0 && (
          <Box style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 10 }}>
            <Chip size="small" label={`${isPt ? 'Condução' : 'Driving'}: ${summary.totalDriveFormatted}`}
              style={{ height: 18, fontSize: '0.55rem', fontWeight: 600, background: 'rgba(59,130,246,0.08)', color: '#3b82f6', border: '1px solid rgba(59,130,246,0.2)' }} />
            {summary.breaks > 0 && (
              <Chip size="small" label={`${summary.breaks} ${isPt ? 'pausa(s)' : 'break(s)'}`}
                style={{ height: 18, fontSize: '0.55rem', fontWeight: 600, background: 'rgba(245,158,11,0.08)', color: '#f59e0b', border: '1px solid rgba(245,158,11,0.2)' }} />
            )}
            {summary.dailyRests > 0 && (
              <Chip size="small" label={`${summary.dailyRests} ${isPt ? 'descanso(s)' : 'rest(s)'}`}
                style={{ height: 18, fontSize: '0.55rem', fontWeight: 600, background: 'rgba(139,92,246,0.08)', color: '#8b5cf6', border: '1px solid rgba(139,92,246,0.2)' }} />
            )}
            {summary.drivingDays > 1 && (
              <Chip size="small" label={`${summary.drivingDays} ${isPt ? 'dias' : 'days'}`}
                style={{ height: 18, fontSize: '0.55rem', fontWeight: 600, background: 'rgba(34,197,94,0.08)', color: '#22c55e', border: '1px solid rgba(34,197,94,0.2)' }} />
            )}
          </Box>
        )}

        {/* Timeline */}
        <Box className={classes.timeline}>
          {segments.map((seg, idx) => {
            const cfg = seg.stopType ? (STOP_CONFIG[seg.stopType] || STOP_CONFIG.break_45) : null;
            const itemClass = seg.type === 'drive' ? classes.driveItem
              : seg.type === 'rest' ? classes.restItem
              : seg.type === 'weekly_rest' ? classes.weeklyRestItem
              : seg.type === 'stop' ? classes.stopItem
              : classes.endItem;

            return (
              <Box key={idx} className={`${classes.timelineItem} ${itemClass}`}>
                <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box style={{ flex: 1 }}>
                    <Box style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <Typography className={classes.label}>{seg.label}</Typography>
                      {seg.preferred && (
                        <Typography style={{ fontSize: '0.5rem', color: '#22c55e', fontWeight: 700 }}>
                          {isPt ? 'PREFERENCIAL' : 'PREFERRED'}
                        </Typography>
                      )}
                      {seg.tachoAdjusted && (
                        <Typography style={{ fontSize: '0.5rem', color: '#f59e0b', fontWeight: 700 }}>
                          {isPt ? 'AJUSTADO' : 'ADJUSTED'}
                        </Typography>
                      )}
                    </Box>
                    {seg.sublabel && <Typography className={classes.sublabel}>{seg.sublabel}</Typography>}
                  </Box>
                  {seg.type !== 'end' && (
                    <Chip
                      label={seg.type === 'drive'
                        ? t(lang, 'driving')
                        : cfg ? (isPt ? cfg.labelPt : cfg.labelEn) : t(lang, 'pause')
                      }
                      size="small"
                      style={{
                        height: 16,
                        fontSize: '0.55rem',
                        fontWeight: 700,
                        background: seg.type === 'drive' ? 'rgba(59,130,246,0.08)' : (cfg?.bg || 'rgba(245,158,11,0.08)'),
                        color: seg.type === 'drive' ? '#3b82f6' : (cfg?.color || '#f59e0b'),
                        border: `1px solid ${seg.type === 'drive' ? 'rgba(59,130,246,0.25)' : (cfg?.border || 'rgba(245,158,11,0.25)')}`,
                        flexShrink: 0,
                      }}
                    />
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>

        {/* Regulation reference */}
        <Box style={{ marginTop: 8, padding: '6px 8px', background: '#f8fafc', borderRadius: 6, border: '1px solid #f1f5f9' }}>
          <Typography style={{ fontSize: '0.55rem', color: '#94a3b8' }}>
            {isPt ? 'Regulamento (CE) n.º 561/2006 — Tempos de condução e repouso' : 'Regulation (EC) No 561/2006 — Driving and rest times'}
          </Typography>
        </Box>
      </GlassCard>
    </Box>
  );
}

export default TachographPanel;
